// file: C:/dartProBackend/dart-admin/src/trainingCoach/CoachTrainingOverviewPage.tsx
import * as React from "react";
import { useNotify, usePermissions, useDataProvider } from "react-admin";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  Stack,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Collapse,
  Badge,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";

import { can } from "../auth/can";
import { useClub } from "../club/ClubContext";
import PlayerIdentityRow from "../profile/PlayerIdentityRow";

/** ---------------------------
 * Types (leicht + detail)
 * -------------------------- */

type PresetKey = "7" | "30" | "90" | "365" | "custom";

type CoachCheckpoint = {
  after_row: number;
  after_entries?: number;
  after_darts?: number;

  segment_points: number;
  segment_rows: number;
  segment_avg: number | null;

  cum_points: number;
  cum_rows: number;
  cum_avg: number | null;

  max_points?: number | null;
  quote_pct?: number | null;
};

type CoachColumn = {
  column_index: number;
  total_points: number;
  rows_played: number;
  end_avg: number | null;
  end_darts?: number | null;
  checkpoints: CoachCheckpoint[];
};

type CoachMetrics = {
  mode: "numeric" | "choices" | "unknown" | "mixed";
  actual_points_total: number;
  max_points_total: number | null;
  quote_pct: number | null;
  end_avg_all: number | null;
  columns: CoachColumn[];
  [k: string]: any;
};

type TemplateMini = {
  id: number;
  name: string;
  type?: string | null;
  rows?: number;
  columns?: number;
  quote_checkpoints?: number[] | null;

  row_labels?: string[] | null;
  column_labels?: string[] | null;

  // optional (backend may include)
  input_mode?: string | null;
  x01_rules?: any | null;
};

type Submission = {
  column_index: number;
  values: Array<number | null>;
  points: number;
  submitted_at?: string | null;
};

type SessionLight = {
  id: number;
  status: string;
  started_at?: string | null;
  finished_at?: string | null;

  template?: TemplateMini | null;
  coach_metrics?: CoachMetrics | null;

  // absichtlich leer im overview
  submissions?: any[];

  [k: string]: any;
};

type SessionDetail = SessionLight & {
  template: TemplateMini;
  submissions: Submission[];
  coach_metrics?: CoachMetrics | null;

  player_id?: number;
  player_name?: string;
  player_first_name?: string;
  player_last_name?: string;
  player_avatar?: string;
  player_points?: number;
};

type ScoreRangesSum = {
  "95_99": number;
  "100_119": number;
  "120_139": number;
  "140_167": number;
  "170": number;
  "171": number;
  "174": number;
  "177": number;
  "180": number;
};

const SCORE_RANGES = [
  { key: "95_99", label: "95–99", test: (v: number) => v >= 95 && v < 100 },
  { key: "100_119", label: "100–119", test: (v: number) => v >= 100 && v < 120 },
  { key: "120_139", label: "120–139", test: (v: number) => v >= 120 && v < 140 },
  { key: "140_167", label: "140–167", test: (v: number) => v >= 140 && v <= 167 },
  { key: "170", label: "170", test: (v: number) => v === 170 },
  { key: "171", label: "171", test: (v: number) => v === 171 },
  { key: "174", label: "174", test: (v: number) => v === 174 },
  { key: "177", label: "177", test: (v: number) => v === 177 },
  { key: "180", label: "180", test: (v: number) => v === 180 },
] as const;

type ScoreRangeKey = (typeof SCORE_RANGES)[number]["key"];
type ScoreRangeCounts = Record<ScoreRangeKey, number>;

const emptyScoreRangeCounts = (): ScoreRangeCounts => ({
  "95_99": 0,
  "100_119": 0,
  "120_139": 0,
  "140_167": 0,
  "170": 0,
  "171": 0,
  "174": 0,
  "177": 0,
  "180": 0,
});

type TemplateGroup = {
  templateId: number;
  templateName: string;
  templateType?: string | null;
  mode: string;

  // numeric/choices aggregates
  avgPoints?: number | null;
  avgQuotePct?: number | null;

  // ✅ numeric: average of coach_metrics.end_avg_all across sessions (or provided by backend)
  avgEndAvgAll?: number | null;

  // X01 extras (optional)
  avgX01AvgPointsPerDart?: number | null;
  avgX01RoundsToFinish?: number | null;
  avgX01DartsToFinish?: number | null;
  avgX01LastThrowMax?: number | null;
  avgX01LastThrowPoints?: number | null;
  scoreRangesSum?: ScoreRangesSum | null;

  sessions: SessionLight[];
};

type PlayerGroup = {
  player_id: number;
  player_name: string;
  player_first_name?: string | null;
  player_last_name?: string | null;
  player_avatar?: string | null;
  player_points: number;
  templates: TemplateGroup[];
};

/** ---------------------------
 * Utils
 * -------------------------- */

function fmtPct(x?: number | null) {
  if (typeof x !== "number") return "-";
  return `${x.toFixed(1)}%`;
}
const fmtNum = (v: number | null | undefined, digits = 2) =>
  v == null || Number.isNaN(v) ? "–" : Number(v).toFixed(digits);

function yyyyMmDd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function calcPresetRange(preset: PresetKey): { from: string; to: string } {
  const today = new Date();
  const to = yyyyMmDd(today);
  if (preset === "custom") return { from: to, to };
  const days = Number(preset);
  const fromDate = new Date(today.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
  return { from: yyyyMmDd(fromDate), to };
}

/** ---------------------------
 * X01 helpers
 * -------------------------- */

function isX01Like(templateType?: string | null) {
  const tt = String(templateType ?? "").trim().toLowerCase();
  return tt === "x01" || tt === "x-01" || tt === "x_01";
}

function isX01Session(s: SessionLight, tg?: TemplateGroup) {
  const tpl: any = s.template ?? null;
  const cm: any = s.coach_metrics ?? null;

  if (isX01Like(tg?.templateType)) return true;
  if (tpl && tpl.x01_rules) return true;
  if (tpl && String(tpl.input_mode ?? "").toLowerCase() === "numeric" && tpl.x01_rules) return true;

  // fallback: coach metrics keys
  if (cm && (typeof cm.x01_start_value === "number" || typeof cm.x01_rounds_to_finish === "number")) return true;

  return false;
}

function avgFromSessions(sessions: SessionLight[], key: string): number | null {
  const vals: number[] = [];
  for (const s of sessions ?? []) {
    const cm: any = s.coach_metrics ?? null;
    const v = cm?.[key];
    if (typeof v === "number" && !Number.isNaN(v)) vals.push(v);
  }
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

const getSessionAllValues = (s: SessionDetail): number[] => {
  const all: number[] = [];
  for (const sub of s.submissions ?? []) {
    if (!Array.isArray(sub.values)) continue;
    for (const v of sub.values) {
      if (typeof v === "number" && !Number.isNaN(v)) all.push(v);
    }
  }
  return all;
};

const computeScoreRangeCounts = (values: Array<number | null | undefined>): ScoreRangeCounts => {
  const out = emptyScoreRangeCounts();
  for (const v of values) {
    if (typeof v !== "number" || Number.isNaN(v)) continue;
    const hit = SCORE_RANGES.find((r) => r.test(v));
    if (hit) out[hit.key] += 1;
  }
  return out;
};

const findSubmission = (s: SessionDetail, columnIndex: number) =>
  (s.submissions ?? []).find((x) => x.column_index === columnIndex) ?? null;

const colLabel = (tpl: TemplateMini | null | undefined, columnIndex: number) => {
  const lbl = tpl?.column_labels?.[columnIndex];
  return lbl ? `${lbl}` : `Spalte ${columnIndex + 1}`;
};

const checkpointToLabel = (mode: "numeric" | "choices" | string, cp: CoachCheckpoint) => {
  if (mode === "numeric") {
    const d = cp.after_darts ?? (cp.after_entries != null ? cp.after_entries * 3 : null);
    return d != null ? `${d} Darts` : `Reihe ${cp.after_row}`;
  }
  const e = cp.after_entries ?? cp.after_row;
  return `${e} Entries`;
};

const segmentLabel = (mode: "numeric" | "choices" | string, from: number, to: number) => {
  if (mode === "numeric") return `${from + 1}–${to} Darts`;
  return `${from + 1}–${to} Entries`;
};

function getSessionX01Ranges(cm: any): ScoreRangesSum | null {
  return cm?.score_ranges_sum ?? cm?.scoreRangesSum ?? null;
}

/** ---------------------------
 * Filters (Draft vs Applied)
 * -------------------------- */

type DraftFilters = {
  status: string;
  preset: PresetKey;
  customFrom: string;
  customTo: string;
  templateId: string;
  playerId: string;
};

function makeDefaultDraft(): DraftFilters {
  const preset: PresetKey = "30";
  const pr = calcPresetRange(preset);
  return {
    status: "all",
    preset,
    customFrom: pr.from,
    customTo: pr.to,
    templateId: "all",
    playerId: "all",
  };
}

function buildOverviewQs(clubId: number, f: DraftFilters): string {
  const pr = calcPresetRange(f.preset);
  const date_from = f.preset === "custom" ? f.customFrom : pr.from;
  const date_to = f.preset === "custom" ? f.customTo : pr.to;

  const qs = new URLSearchParams();
  qs.set("club_id", String(clubId));
  qs.set("date_from", date_from);
  qs.set("date_to", date_to);

  if (f.status !== "all") qs.set("status", f.status);
  if (f.templateId !== "all") qs.set("template_id", f.templateId);
  if (f.playerId !== "all") qs.set("player_id", f.playerId);

  qs.set("include_score_ranges", "true");
  return qs.toString();
}

function countActiveFilters(f: DraftFilters): number {
  let c = 0;
  if (f.status !== "all") c++;
  if (f.preset !== "30") c++;
  if (f.preset === "custom") c++;
  if (f.templateId !== "all") c++;
  if (f.playerId !== "all") c++;
  return c;
}

/** ---------------------------
 * Page
 * -------------------------- */

export default function CoachTrainingOverviewPage() {
  const { clubId } = useClub();
  const notify = useNotify();
  const dp = useDataProvider();
  const queryClient = useQueryClient();

  const isMobile = useMediaQuery("(max-width:600px)");
  const { permissions, isPending } = usePermissions();

  // Draft (UI)
  const [draft, setDraft] = React.useState<DraftFilters>(() => makeDefaultDraft());
  const [filtersOpenMobile, setFiltersOpenMobile] = React.useState(false);

  // Applied (drives query)
  const [applied, setApplied] = React.useState<DraftFilters>(() => makeDefaultDraft());

  React.useEffect(() => {
    if (draft.preset !== "custom") {
      const pr = calcPresetRange(draft.preset);
      setDraft((d) => ({ ...d, customFrom: pr.from, customTo: pr.to }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.preset]);

  // Expand states
  const [openPlayerId, setOpenPlayerId] = React.useState<number | null>(null);
  const [openTemplateKey, setOpenTemplateKey] = React.useState<string | null>(null);
  const [openSessionId, setOpenSessionId] = React.useState<number | null>(null);

  // Delete dialog
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [sessionToDelete, setSessionToDelete] = React.useState<SessionLight | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const askDelete = (s: SessionLight) => {
    setSessionToDelete(s);
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    if (!sessionToDelete || !clubId) return;
    setDeleting(true);

    try {
      const res = await (dp as any).custom(`training/sessions/${sessionToDelete.id}?club_id=${clubId}`, {
        method: "DELETE",
      });

      const msg = (res.data as any)?.detail ?? (res.data as any)?.message ?? "Training gelöscht.";
      notify(msg, { type: "info" });

      setConfirmOpen(false);
      setSessionToDelete(null);

      queryClient.invalidateQueries({ queryKey: ["coachOverviewGrouped"] });
      queryClient.invalidateQueries({ queryKey: ["coachSessionDetail"] });
    } catch (e: any) {
      const msg = e?.body?.detail ?? e?.body?.message ?? e?.message ?? "Löschen fehlgeschlagen.";
      notify(msg, { type: "warning" });
    } finally {
      setDeleting(false);
    }
  };

  const appliedQs = React.useMemo(() => {
    if (!clubId) return "";
    return buildOverviewQs(clubId, applied);
  }, [clubId, applied]);

  const overviewQuery = useQuery<PlayerGroup[], Error>({
    queryKey: ["coachOverviewGrouped", clubId ?? 0, appliedQs],
    enabled: !!clubId && appliedQs.length > 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    // ⚠️ initialData raus, damit isLoading sauber funktioniert und man „echtes Laden“ sieht
    queryFn: async () => {
      const res = await (dp as any).custom(`training/coach/overview?${appliedQs}`, { method: "GET" });
      return (res.data as PlayerGroup[]) ?? [];
    },
  });

  React.useEffect(() => {
    if (!overviewQuery.isError) return;
    const e: any = overviewQuery.error;
    const msg = e?.body?.detail ?? e?.body?.message ?? e?.message ?? "Overview konnte nicht geladen werden.";
    notify(msg, { type: "warning" });
  }, [overviewQuery.isError, overviewQuery.error, notify]);

  const players: PlayerGroup[] = overviewQuery.data ?? [];
  const overviewLoading = overviewQuery.isLoading || overviewQuery.isFetching;

  const playerOptions = React.useMemo(() => {
    return [...players]
      .map((p: PlayerGroup) => ({
        id: String(p.player_id),
        label: p.player_name ?? `User ${p.player_id}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "de"));
  }, [players]);

  const templateOptions = React.useMemo(() => {
    const map = new Map<number, string>();
    for (const p of players) {
      for (const tg of p.templates ?? []) {
        if (typeof tg.templateId === "number") {
          map.set(tg.templateId, tg.templateName ?? `Template ${tg.templateId}`);
        }
      }
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id: String(id), label: name }))
      .sort((a, b) => a.label.localeCompare(b.label, "de"));
  }, [players]);

  const appliedActiveFilterCount = React.useMemo(() => countActiveFilters(applied), [applied]);
  const draftDiffersFromApplied = React.useMemo(() => JSON.stringify(draft) !== JSON.stringify(applied), [draft, applied]);

  const applyFilters = React.useCallback(() => {
    setOpenPlayerId(null);
    setOpenTemplateKey(null);
    setOpenSessionId(null);
    setApplied(draft);
  }, [draft]);

  const resetFilters = React.useCallback(() => {
    const def = makeDefaultDraft();
    setDraft(def);
    setApplied(def);
    setOpenPlayerId(null);
    setOpenTemplateKey(null);
    setOpenSessionId(null);
  }, []);

  const sessionDetailQuery = useQuery<SessionDetail | null, Error>({
    queryKey: ["coachSessionDetail", clubId ?? 0, openSessionId ?? 0],
    enabled: !!clubId && !!openSessionId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: "always",
    staleTime: 0,
    placeholderData: keepPreviousData,
    initialData: null,
    queryFn: async () => {
      const res = await (dp as any).custom(
        `training/coach/sessions/${openSessionId}?club_id=${clubId}&include=full`,
        { method: "GET" }
      );
      return (res.data as SessionDetail) ?? null;
    },
  });

  React.useEffect(() => {
    if (!sessionDetailQuery.isError) return;
    const e: any = sessionDetailQuery.error;
    const msg = e?.body?.detail ?? e?.body?.message ?? e?.message ?? "Session-Details konnten nicht geladen werden.";
    notify(msg, { type: "warning" });
  }, [sessionDetailQuery.isError, sessionDetailQuery.error, notify]);

  const FiltersContent = (
    <>
      <TextField
        select
        label="Zeitraum"
        size="small"
        value={draft.preset}
        onChange={(e) => setDraft((d) => ({ ...d, preset: e.target.value as PresetKey }))}
        sx={{ minWidth: isMobile ? undefined : 200 }}
      >
        <MenuItem value="7">Letzte 7 Tage</MenuItem>
        <MenuItem value="30">Letzte 30 Tage</MenuItem>
        <MenuItem value="90">Letzte 90 Tage</MenuItem>
        <MenuItem value="365">Letzte 365 Tage</MenuItem>
        <MenuItem value="custom">Benutzerdefiniert</MenuItem>
      </TextField>

      {draft.preset === "custom" && (
        <>
          <TextField
            label="Von"
            type="date"
            value={draft.customFrom}
            onChange={(e) => setDraft((d) => ({ ...d, customFrom: e.target.value }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Bis"
            type="date"
            value={draft.customTo}
            onChange={(e) => setDraft((d) => ({ ...d, customTo: e.target.value }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </>
      )}

      <TextField
        select
        label="Status"
        size="small"
        value={draft.status}
        onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
        sx={{ minWidth: isMobile ? undefined : 160 }}
      >
        <MenuItem value="all">Alle</MenuItem>
        <MenuItem value="in_progress">Offen</MenuItem>
        <MenuItem value="finished">Beendet</MenuItem>
        <MenuItem value="aborted">Abgebrochen</MenuItem>
        <MenuItem value="cancelled">Cancelled</MenuItem>
      </TextField>

      <TextField
        select
        label="Spiel"
        size="small"
        value={draft.templateId}
        onChange={(e) => setDraft((d) => ({ ...d, templateId: e.target.value }))}
        sx={{ minWidth: isMobile ? undefined : 260, maxWidth: isMobile ? undefined : 420 }}
      >
        <MenuItem value="all">Alle</MenuItem>
        {templateOptions.map((t) => (
          <MenuItem key={t.id} value={t.id}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Spieler"
        size="small"
        value={draft.playerId}
        onChange={(e) => setDraft((d) => ({ ...d, playerId: e.target.value }))}
        sx={{ minWidth: isMobile ? undefined : 220, maxWidth: isMobile ? undefined : 360 }}
      >
        <MenuItem value="all">Alle</MenuItem>
        {playerOptions.map((p) => (
          <MenuItem key={p.id} value={p.id}>
            {p.label}
          </MenuItem>
        ))}
      </TextField>

      <Button
        size="small"
        variant="contained"
        onClick={applyFilters}
        disabled={overviewLoading || !clubId || !draftDiffersFromApplied}
      >
        Anwenden
      </Button>

      <Button
        size="small"
        variant="outlined"
        onClick={() => queryClient.invalidateQueries({ queryKey: ["coachOverviewGrouped"] })}
        disabled={overviewLoading || !clubId}
      >
        Neu laden
      </Button>

      <Button size="small" variant="text" onClick={resetFilters} disabled={overviewLoading || !clubId}>
        Reset
      </Button>

      <Chip size="small" label={`${players.length} Spieler`} variant="outlined" />
    </>
  );

  // ✅ nicht mehr "leere Seite" beim Pending, sondern sichtbarer Loader
  if (isPending) {
    return (
      <Stack sx={{ p: 2 }} spacing={1} direction="row" alignItems="center">
        <CircularProgress size={18} />
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Lade Berechtigungen…
        </Typography>
      </Stack>
    );
  }

  return (
    <>
      <Stack spacing={2} sx={{ p: 2, width: "100%" }}>
        <Typography variant="h5" fontWeight={800}>
          Statistik Training
        </Typography>

        <Card sx={{ borderRadius: 1 }}>
          <CardContent>
            {isMobile ? (
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1 }}>
                  <Typography fontWeight={900}>Filter</Typography>

                  <Badge
                    badgeContent={appliedActiveFilterCount}
                    color="primary"
                    overlap="circular"
                    invisible={appliedActiveFilterCount === 0}
                  >
                    <Button
                      size="small"
                      variant={filtersOpenMobile ? "contained" : "outlined"}
                      startIcon={<FilterListIcon />}
                      onClick={() => setFiltersOpenMobile((v) => !v)}
                    >
                      {filtersOpenMobile ? "Zuklappen" : "Anzeigen"}
                    </Button>
                  </Badge>
                </Stack>

                <Collapse in={filtersOpenMobile} timeout="auto" unmountOnExit>
                  <Stack direction="column" spacing={1.5} sx={{ alignItems: "stretch" }}>
                    {FiltersContent}
                  </Stack>
                </Collapse>

                {!filtersOpenMobile && (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    <Chip label={`${players.length} Spieler`} />
                    {applied.status !== "all" && <Chip size="small" label="Status" />}
                    {applied.templateId !== "all" && <Chip size="small" label="Spiel" />}
                    {applied.playerId !== "all" && <Chip size="small" label="Spieler" />}
                    {applied.preset !== "30" && <Chip size="small" label="Zeitraum" />}
                    {overviewLoading && <Chip size="small" label="Lädt…" />}
                    {draftDiffersFromApplied && <Chip size="small" color="warning" label="Filter nicht angewendet" />}
                  </Stack>
                )}
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                {FiltersContent}
                {draftDiffersFromApplied && (
                  <Chip size="small" color="warning" label="Filter geändert (noch nicht angewendet)" />
                )}
              </Stack>
            )}
          </CardContent>
        </Card>

        {overviewLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Lade Overview…
            </Typography>
          </Stack>
        ) : players.length === 0 ? (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Keine Daten im gewählten Zeitraum.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {players.map((p: PlayerGroup) => {
              const totalTrainings = p.templates.reduce(
                (a: number, t: TemplateGroup) => a + (t.sessions?.length ?? 0),
                0
              );
              const isOpen = openPlayerId === p.player_id;

              return (
                <Accordion
                  key={p.player_id}
                  disableGutters
                  expanded={isOpen}
                  onChange={(_, next) => {
                    setOpenTemplateKey(null);
                    setOpenSessionId(null);
                    setOpenPlayerId(next ? p.player_id : null);
                  }}
                  sx={{ borderRadius: 1, overflow: "hidden", "&:before": { display: "none" } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%", flexWrap: "wrap" }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <PlayerIdentityRow
                          firstName={p.player_first_name ?? p.player_name}
                          lastName={p.player_last_name ?? undefined}
                          email={undefined}
                          points={p.player_points}
                          avatarUrl={p.player_avatar ?? undefined}
                          showPoints={false}
                          size="sm"
                        />
                      </Box>

                      <Chip size="small" label={`${totalTrainings} Training(s)`} />
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Divider sx={{ mb: 2 }} />

                    <Stack spacing={1.2}>
                      {p.templates.map((tg: TemplateGroup) => {
                        const tplKey = `${p.player_id}::tpl::${tg.templateId}`;
                        const tplOpen = openTemplateKey === tplKey;

                        const groupSampleSession = tg.sessions?.[0] ?? null;
                        const isX01Group =
                          isX01Like(tg.templateType) || (groupSampleSession ? isX01Session(groupSampleSession, tg) : false);

                        const avgRounds =
                          tg.avgX01RoundsToFinish ??
                          (isX01Group ? avgFromSessions(tg.sessions, "x01_rounds_to_finish") : null);
                        const avgDarts =
                          tg.avgX01DartsToFinish ??
                          (isX01Group ? avgFromSessions(tg.sessions, "x01_darts_to_finish") : null);
                        const avgLastMax =
                          tg.avgX01LastThrowMax ??
                          (isX01Group ? avgFromSessions(tg.sessions, "x01_last_throw_max") : null);

                        const avgPerRound =
                          tg.avgX01AvgPointsPerDart ??
                          (isX01Group ? avgFromSessions(tg.sessions, "x01_avg_points_per_dart") : null);

                        // ✅ numeric: Ø End-Avg gesamt (oberste Ebene) – backend Feld oder fallback aus Sessions
                        const avgEndAvgAll =
                          tg.avgEndAvgAll ??
                          (!isX01Group && tg.mode === "numeric" ? avgFromSessions(tg.sessions, "end_avg_all") : null);

                        return (
                          <Accordion
                            key={tplKey}
                            disableGutters
                            expanded={tplOpen}
                            onChange={(_, next) => {
                              setOpenSessionId(null);
                              setOpenTemplateKey(next ? tplKey : null);
                            }}
                            sx={{ borderRadius: 1, overflow: "hidden" }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", width: "100%" }}>
                                <Typography fontWeight={900} sx={{ mr: 1 }}>
                                  {tg.templateName}
                                </Typography>

                                <Chip size="small" label={`${tg.sessions.length}x`} />

                                {isX01Group ? (
                                  <>
                                    {avgRounds != null ? (
                                      <Chip size="small" color="primary" label={`Ø Runden bis Finish: ${fmtNum(avgRounds, 1)}`} />
                                    ) : null}
                                    {avgDarts != null ? (
                                      <Chip
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        label={`Ø Darts bis Finish: ${fmtNum(avgDarts, 0)}`}
                                      />
                                    ) : null}
                                    {avgLastMax != null ? (
                                      <Chip size="small" label={`Ø letzter Wurf (max): ${fmtNum(avgLastMax, 0)}`} />
                                    ) : null}
                                    {avgPerRound != null ? (
                                      <Chip size="small" label={`Ø Wurfwert/Runde: ${fmtNum(avgPerRound, 1)}`} />
                                    ) : null}
                                  </>
                                ) : (
                                  <>
                                    {tg.avgPoints != null ? (
                                      <Chip size="small" label={`Ø Punkte: ${fmtNum(tg.avgPoints, 1)}`} />
                                    ) : null}

                                    {tg.mode === "numeric" && avgEndAvgAll != null ? (
                                      <Chip
                                        size="small"
                                        color="primary"
                                        label={`Ø End-Avg gesamt: ${fmtNum(avgEndAvgAll, 2)}`}
                                      />
                                    ) : null}

                                    {tg.mode === "choices" && tg.avgQuotePct != null ? (
                                      <Chip size="small" color="primary" label={`Ø Endquote: ${fmtPct(tg.avgQuotePct)}`} />
                                    ) : null}
                                  </>
                                )}

                                {tg.mode === "numeric" && tg.scoreRangesSum ? (
                                  <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
                                    {SCORE_RANGES.map((r) => (
                                      <Chip
                                        key={`tpl-${tg.templateId}-sr-${r.key}`}
                                        size="small"
                                        variant="outlined"
                                        label={`${r.label}: ${tg.scoreRangesSum?.[r.key as keyof ScoreRangesSum] ?? 0}`}
                                      />
                                    ))}
                                  </Stack>
                                ) : null}
                              </Stack>
                            </AccordionSummary>

                            <AccordionDetails>
                              {/* ... Rest unverändert ... */}
                              <Stack spacing={1.2}>
                                {tg.sessions.map((s: SessionLight) => {
                                  const cm = s.coach_metrics ?? null;
                                  const sessionOpen = openSessionId === s.id;

                                  const detail: SessionDetail | null = sessionOpen ? sessionDetailQuery.data : null;
                                  const detailLoading = sessionOpen && sessionDetailQuery.isFetching;

                                  const isX01 = isX01Session(s, tg);

                                  return (
                                    <Accordion
                                      key={s.id}
                                      disableGutters
                                      expanded={sessionOpen}
                                      onChange={(_, next) => {
                                        setOpenSessionId(next ? s.id : null);
                                      }}
                                      sx={{ borderRadius: 1, overflow: "hidden" }}
                                    >
                                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", width: "100%" }}>
                                          <Typography fontWeight={900} sx={{ mr: 1 }}>
                                            Training #{s.id}
                                          </Typography>

                                          {can(permissions, "training", "delete") && (
                                            <Tooltip title="Session löschen">
                                              <span>
                                                <IconButton
                                                  color="error"
                                                  size="small"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    askDelete(s);
                                                  }}
                                                  disabled={deleting}
                                                >
                                                  <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                          )}

                                          <Chip size="small" label={s.status} />

                                          {!isX01 && cm?.mode === "choices"
                                            ? cm.quote_pct != null
                                              ? <Chip size="small" label={`Endquote: ${fmtPct(cm.quote_pct)}`} color="primary" />
                                              : <Chip size="small" label="Endquote: –" />
                                            : null}

                                          {!isX01 && cm?.mode === "numeric"
                                            ? cm?.end_avg_all != null
                                              ? <Chip size="small" label={`End-Avg gesamt: ${fmtNum(cm.end_avg_all, 2)}`} color="primary" />
                                              : <Chip size="small" label="End-Avg gesamt: –" />
                                            : null}

                                          {!isX01 ? <Chip size="small" label={`Punkte: ${cm?.actual_points_total ?? "-"}`} /> : null}

                                          {typeof (cm as any)?.x01_rounds_to_finish === "number" ? (
                                            <Chip size="small" color="primary" label={`Runden bis Finish: ${fmtNum((cm as any).x01_rounds_to_finish, 0)}`} />
                                          ) : null}
                                          {typeof (cm as any)?.x01_darts_to_finish === "number" ? (
                                            <Chip size="small" color="primary" variant="outlined" label={`Darts bis Finish: ${fmtNum((cm as any).x01_darts_to_finish, 0)}`} />
                                          ) : null}
                                          {typeof (cm as any)?.x01_last_throw_max === "number" ? (
                                            <Chip size="small" variant="outlined" label={`Letzter Wurf (max): ${fmtNum((cm as any).x01_last_throw_max, 0)}`} />
                                          ) : null}
                                          {typeof (cm as any)?.x01_avg_points_per_dart === "number" ? (
                                            <Chip size="small" variant="outlined" label={`Ø Wurfwert/Runde: ${fmtNum((cm as any).x01_avg_points_per_dart, 1)}`} />
                                          ) : null}

                                          {getSessionX01Ranges(cm) ? (
                                            <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
                                              {SCORE_RANGES.map((r) => (
                                                <Chip
                                                  key={`x01sess-${s.id}-${r.key}`}
                                                  size="small"
                                                  variant="outlined"
                                                  label={`${r.label}: ${getSessionX01Ranges(cm)?.[r.key as keyof ScoreRangesSum] ?? 0}`}
                                                />
                                              ))}
                                            </Stack>
                                          ) : null}

                                          {detailLoading && <Chip size="small" variant="outlined" label="Details laden…" />}
                                        </Stack>
                                      </AccordionSummary>

                                      <AccordionDetails>
                                        {!sessionOpen ? null : detailLoading ? (
                                          <Stack direction="row" spacing={1} alignItems="center">
                                            <CircularProgress size={16} />
                                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                              Details werden geladen…
                                            </Typography>
                                          </Stack>
                                        ) : !detail ? (
                                          <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                            Keine Details verfügbar.
                                          </Typography>
                                        ) : (
                                          <Stack spacing={2}>
                                            {detail.coach_metrics?.mode === "numeric" || isX01 ? (
                                              <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
                                                {(() => {
                                                  const counts = computeScoreRangeCounts(getSessionAllValues(detail));
                                                  return SCORE_RANGES.map((r) => (
                                                    <Chip
                                                      key={`sess-${detail.id}-sr-${r.key}`}
                                                      size="small"
                                                      variant="outlined"
                                                      label={`${r.label}: ${counts[r.key] ?? 0}`}
                                                    />
                                                  ));
                                                })()}
                                              </Stack>
                                            ) : null}

                                            {!detail.coach_metrics ? (
                                              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                                Keine Coach-Metriken vorhanden.
                                              </Typography>
                                            ) : (
                                              <Stack spacing={2}>
                                                {detail.coach_metrics.columns.map((col: CoachColumn) => {
                                                  const sub = findSubmission(detail, col.column_index);
                                                  const values = sub?.values ?? [];

                                                  return (
                                                    <Card key={col.column_index} variant="outlined" sx={{ p: 2 }}>
                                                      <Stack spacing={1}>
                                                        <Typography fontWeight={900}>{colLabel(detail.template, col.column_index)}</Typography>

                                                        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                                                          <Chip size="small" label={`Rows: ${col.rows_played}`} />
                                                          {!isX01 ? <Chip size="small" label={`Punkte: ${col.total_points}`} /> : null}
                                                          {!isX01 ? (
                                                            <Chip
                                                              size="small"
                                                              label={
                                                                detail.coach_metrics?.mode === "numeric"
                                                                  ? `End-Avg (bis ${col.end_darts ?? "-"} Darts): ${fmtNum(col.end_avg)}`
                                                                  : `End-Avg (Entries): ${fmtNum(col.end_avg)}`
                                                              }
                                                            />
                                                          ) : null}
                                                        </Stack>

                                                        {sub ? (
                                                          <>
                                                            <Divider sx={{ my: 1.5 }} />
                                                            <Typography fontWeight={800}>Ergebnisse</Typography>

                                                            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 1 }}>
                                                              {(values ?? ([] as Array<number | null>)).map((v: number | null, idx: number) => {
                                                                const rowLabel = detail.template?.row_labels?.[idx] ?? null;
                                                                const label = rowLabel ? `${rowLabel}: ${v ?? "–"}` : `${idx + 1}: ${v ?? "–"}`;
                                                                return <Chip key={idx} size="small" label={label} variant="outlined" />;
                                                              })}
                                                            </Stack>
                                                          </>
                                                        ) : (
                                                          <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
                                                            Noch keine Ergebnisse für diese Spalte.
                                                          </Typography>
                                                        )}

                                                        {!isX01 && col.checkpoints?.length ? (
                                                          <>
                                                            <Divider sx={{ my: 1.5 }} />
                                                            <Typography fontWeight={800}>Zwischenwerte</Typography>

                                                            <Stack spacing={1}>
                                                              {col.checkpoints.map((cp: CoachCheckpoint, i: number) => {
                                                                const prev = col.checkpoints[i - 1];
                                                                const toLabel = checkpointToLabel(detail.coach_metrics!.mode, cp);

                                                                const prevTo =
                                                                  detail.coach_metrics!.mode === "numeric"
                                                                    ? prev?.after_darts ?? 0
                                                                    : prev?.after_entries ?? 0;

                                                                const curTo =
                                                                  detail.coach_metrics!.mode === "numeric"
                                                                    ? cp.after_darts ?? prevTo
                                                                    : cp.after_entries ?? prevTo;

                                                                return (
                                                                  <Stack
                                                                    key={`${cp.after_row}-${i}`}
                                                                    direction="row"
                                                                    spacing={1}
                                                                    sx={{ flexWrap: "wrap", alignItems: "center" }}
                                                                  >
                                                                    <Chip size="small" label={`Ø bis ${toLabel}: ${fmtNum(cp.cum_avg)}`} />
                                                                    <Chip
                                                                      size="small"
                                                                      variant="outlined"
                                                                      label={`${segmentLabel(detail.coach_metrics!.mode, prevTo, curTo)}: ${fmtNum(cp.segment_avg)}`}
                                                                    />
                                                                    {detail.coach_metrics!.mode === "choices" && cp.quote_pct != null ? (
                                                                      <Chip size="small" color="primary" label={`Quote: ${fmtNum(cp.quote_pct, 1)}%`} />
                                                                    ) : null}
                                                                  </Stack>
                                                                );
                                                              })}
                                                            </Stack>
                                                          </>
                                                        ) : null}
                                                      </Stack>
                                                    </Card>
                                                  );
                                                })}
                                              </Stack>
                                            )}
                                          </Stack>
                                        )}
                                      </AccordionDetails>
                                    </Accordion>
                                  );
                                })}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        )}
      </Stack>

      <Dialog open={confirmOpen} onClose={() => (deleting ? null : setConfirmOpen(false))}>
        <DialogTitle>Training löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Willst du diese Session wirklich löschen? Das kann nicht rückgängig gemacht werden.
            <br />
            <b>Session #{sessionToDelete?.id}</b>
            {sessionToDelete?.status === "in_progress" ? " (offen)" : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            Abbrechen
          </Button>
          <Button onClick={doDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? "Lösche…" : "Löschen"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
